"use client"

import { NewTabLink } from "./new-tab-link"
import { ResourceLink } from "./resource-link"
import Link from "next/link"
import { ArrowLeft, ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { STEPS } from "@/lib/breach/steps"
import type { GoTo } from "./incident-nav"
import type { WorkspaceUpdater } from "./incident-tools"

/**
 * Two short explainer pages shown after intake (and re-readable from the plan
 * index). Plain language, no jargon; each page ends with the disclaimer line.
 */
export function IntroPages({ update, page, go }: {
  update: WorkspaceUpdater; page: 0 | 1; go: GoTo
}) {
  const finish = () => {
    update((x) => ({ ...x, introSeen: true, updatedAt: new Date().toISOString() }))
    go({ v: "index" })
  }

  return (
    <div className="grid gap-6">
      {page === 0 ? <IntroPageOne /> : <IntroPageTwo go={go} />}

      <p className="text-xs text-muted-foreground">
        General information only — not legal advice.{" "}
        <NewTabLink href="/disclaimer" className="underline hover:text-foreground">Read the full disclaimer</NewTabLink>.
      </p>

      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <Button variant="outline" size="lg" disabled={page === 0} onClick={() => go({ v: "intro", p: 0 })}>
          <ArrowLeft /> Back
        </Button>
        <div className="flex flex-wrap items-center gap-4">
          <button type="button"
            className="rounded text-sm text-muted-foreground underline hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-solid"
            onClick={finish}>
            Skip to my plan
          </button>
          {page === 0 ? (
            <Button size="lg" className="bg-accent-solid text-accent-solid-foreground hover:bg-accent-solid/90"
              onClick={() => go({ v: "intro", p: 1 })}>
              Next <ArrowRight />
            </Button>
          ) : (
            <Button size="lg" className="bg-accent-solid text-accent-solid-foreground hover:bg-accent-solid/90"
              onClick={finish}>
              Start my plan <ArrowRight />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

function IntroPageOne() {
  return (
    <div className="grid gap-4">
      <div>
        <p className="text-sm font-medium text-muted-foreground">Intro · Page 1 of 2</p>
        <h1 className="mt-1 text-2xl font-bold sm:text-3xl">What you&apos;re about to do</h1>
      </div>
      <p className="text-muted-foreground">
        This is a <strong className="text-foreground">Day-1 guide</strong> for organizations that don&apos;t already have an
        incident response plan. If you have a tested plan, follow that instead.
      </p>
      <p className="text-muted-foreground">
        It takes you through <strong className="text-foreground">nine steps</strong>. You can work through them in order, or
        jump straight to the one you need right now.
      </p>
      <ol className="grid gap-2 rounded-lg border border-border bg-card p-4 text-sm">
        {STEPS.map((s) => (
          <li key={s.id} className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-semibold text-muted-foreground">{s.n}.</span>
            <span className="font-medium">{s.title}</span>
            <span className="text-xs text-muted-foreground">({s.minutes})</span>
          </li>
        ))}
      </ol>
      <ul className="grid gap-2 text-sm text-muted-foreground">
        <li>The tool keeps a <span className="text-foreground">running log</span> of what you did and when — see the Timeline at any time.</li>
        <li>Unless you choose to sign in, everything you enter stays <span className="text-foreground">in this browser only</span> and isn't sent to us.</li>
        <li>It is <span className="text-foreground">general information, not legal advice</span>. Your lawyer makes the legal calls.</li>
      </ul>
      <p className="rounded-md border border-destructive/60 bg-destructive/10 p-3 text-sm font-semibold text-destructive">
        If anyone is in danger, or a crime is happening right now, call 911 first.
      </p>
    </div>
  )
}

function IntroPageTwo({ go }: { go: GoTo }) {
  return (
    <div className="grid gap-4">
      <div>
        <p className="text-sm font-medium text-muted-foreground">Intro · Page 2 of 2</p>
        <h1 className="mt-1 text-2xl font-bold sm:text-3xl">Ground rules for the next few hours</h1>
      </div>
      <ul className="grid gap-3 text-sm">
        <li className="rounded-md border border-destructive/60 bg-destructive/10 p-3">
          <strong>Do not wipe, reformat, reinstall, or delete anything</strong> on affected systems. That destroys the evidence you will need.
        </li>
        <li className="rounded-md border border-yellow-500/50 bg-yellow-500/10 p-3">
          <strong>Talk by phone.</strong> Assume attackers may be reading your email and chat. Use phones or another channel you trust.
        </li>
        <li className="rounded-md border border-border bg-card p-3">
          <strong>Call your <ResourceLink href="/find-a-lawyer" className="underline">lawyer</ResourceLink> and your{" "}
          <ResourceLink href="/insurance" className="underline">insurer</ResourceLink> early.</strong> Notification decisions have legal consequences, and policies often require prompt notice.
        </li>
        <li className="rounded-md border border-border bg-card p-3">
          <strong>Use the <ResourceLink href="/directory" className="underline">Resources</ResourceLink>.</strong> Throughout the plan you&apos;ll see links to directories: who to report to, platform contacts, lawyers, insurance, and more.
        </li>
        <li className="rounded-md border border-border bg-card p-3">
          <strong>Write everything down with times.</strong> Use the <button type="button" className="underline" onClick={() => go({ v: "timeline" })}>Timeline</button> in this tool; regulators, insurers, and your lawyer will ask.
        </li>
        <li className="rounded-md border border-border bg-card p-3">
          <strong>Use confirmed facts only.</strong> Don&apos;t speculate about cause, attacker, or numbers — especially in anything you send outside the company.
        </li>
        <li className="rounded-md border border-border bg-card p-3">
          <strong>Coverage note:</strong> this tool covers NIST guidance and <strong>US federal</strong> rules only. State breach-notification laws and laws outside the US also apply and are <strong>not yet included</strong>. Ask your lawyer.
        </li>
        <li className="rounded-md border border-border bg-card p-3">
          <strong>The deadline clocks</strong> (on the right, or at the top on a phone) are <strong>conservative estimates</strong> — the earliest a deadline could possibly fall. Confirm each one with your lawyer. You can adjust the dates, hide clocks that don&apos;t apply, or add your own.
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        Some clocks only start once you <span className="text-foreground">know</span> whether someone got in without
        permission and whether information was taken. &ldquo;I don&apos;t know&rdquo; is an okay answer — those clocks
        stay marked <span className="text-foreground">Not started</span> and the tool keeps reminding you until you
        answer. You can change any answer at any time under <span className="text-foreground">My answers</span>.
      </p>
    </div>
  )
}
